import { useEffect, useState, useRef, useCallback } from 'react';

import { useAccessibility } from "@/components/ui/AccessibilityProvider";

interface CustomCursorProps {
  disabled?: boolean;
}

const CustomCursor = ({ disabled = true }: CustomCursorProps) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState('default');
  const [shouldRender, setShouldRender] = useState(false);
  const { reducedMotionEnabled, customCursorEnabled } = useAccessibility();

  const isCursorEnabled = customCursorEnabled && !disabled;
  
  // PERFORMANCE FIX P1: Use refs for DOM manipulation instead of state
  const cursorRef = useRef<HTMLDivElement>(null);
  const followerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | undefined>(undefined);

  // PERFORMANCE FIX P1: Throttled mouse move using RAF + direct DOM manipulation
  const mouseMove = useCallback((e: MouseEvent) => {
    if (!isCursorEnabled) {
      return;
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const newPosition = { x: e.clientX, y: e.clientY };
      setMousePosition(newPosition);
      
      // Direct DOM manipulation to avoid React renders
      if (cursorRef.current && followerRef.current) {
        const scale = cursorVariant === 'text' ? 1.5 : 1;
        cursorRef.current.style.transform = `translate(-50%, -50%) scale(${scale})`;
        followerRef.current.style.transform = `translate(-50%, -50%) scale(${scale * 0.8})`;
        cursorRef.current.style.left = `${newPosition.x}px`;
        cursorRef.current.style.top = `${newPosition.y}px`;
        followerRef.current.style.left = `${newPosition.x}px`;
        followerRef.current.style.top = `${newPosition.y}px`;
      }
    });
  }, [cursorVariant, isCursorEnabled]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      reducedMotionEnabled ||
      !isCursorEnabled
    ) {
      setShouldRender(false);
      return;
    }

    // Check user preferences and disabled prop
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const isTouchDevice = window.matchMedia?.('(pointer: coarse)').matches ?? false;

    // ACCESSIBILITY: Check if user is navigating with keyboard
    let isKeyboardUser = false;
    const handleKeyboardNav = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        isKeyboardUser = true;
        setShouldRender(false);
      }
    };
    
    // Don't render custom cursor if disabled, for touch devices, users who prefer reduced motion, or keyboard users
    if (prefersReducedMotion || isTouchDevice) {
      setShouldRender(false);
      return;
    }

    // Listen for keyboard navigation
    window.addEventListener('keydown', handleKeyboardNav);

    const handlePointerInput = () => {
      if (isKeyboardUser) {
        isKeyboardUser = false;
        setShouldRender(true);
      }
    };

    setShouldRender(true);

    window.addEventListener('mousemove', mouseMove);
    window.addEventListener('pointerdown', handlePointerInput);
    window.addEventListener('pointermove', handlePointerInput);

    // Add hover effects for interactive elements using event delegation for performance
    const handleMouseOver = (e: Event) => {
      const target = e.target as Element;
      if (target.matches('a, button, [role="button"]')) {
        setCursorVariant('text');
      }
    };

    const handleMouseOut = (e: Event) => {
      const target = e.target as Element;
      if (target.matches('a, button, [role="button"]')) {
        setCursorVariant('default');
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      window.removeEventListener('mousemove', mouseMove);
      window.removeEventListener('pointerdown', handlePointerInput);
      window.removeEventListener('pointermove', handlePointerInput);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      window.removeEventListener('keydown', handleKeyboardNav);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isCursorEnabled, mouseMove, reducedMotionEnabled]);

  // Honor keyboard focus for accessibility
  useEffect(() => {
    if (typeof document === 'undefined' || !isCursorEnabled) {
      return;
    }

    const handleFocusIn = () => setCursorVariant('text');
    const handleFocusOut = () => setCursorVariant('default');

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isCursorEnabled]);

  // Don't render if user preferences indicate it shouldn't be shown
  if (!shouldRender) {
    return null;
  }

  const variants = {
    default: {
      scale: 1,
    },
    text: {
      scale: 1.5,
    },
  };

  return (
    <>
      <div
        ref={cursorRef}
        className="cursor"
        style={{
          left: `${mousePosition.x}px`,
          top: `${mousePosition.y}px`,
          transform: `translate(-50%, -50%) scale(${variants[cursorVariant as keyof typeof variants].scale})`,
        }}
        aria-hidden="true"
      />
      <div
        ref={followerRef}
        className="cursor-follower"
        style={{
          left: `${mousePosition.x}px`,
          top: `${mousePosition.y}px`,
          transform: `translate(-50%, -50%) scale(${variants[cursorVariant as keyof typeof variants].scale * 0.8})`,
        }}
        aria-hidden="true"
      />
    </>
  );
};

export default CustomCursor;